import API from "@utils/API/API";
import { normalizeApproved, translateInclude } from "./normalizeApproved";

// Non-hook catalog reads for legacy JS callers that fetch outside React Query.
// `approved` comes from flags.releaseApprovedServices at the call site.

interface ListArgs {
  accessToken: string;
  approved: boolean;
  include?: string[];
  filter?: Record<string, unknown>;
  page?: { number: number; size: number };
  sort?: string[];
}

interface ShowArgs {
  accessToken: string;
  approved: boolean;
  id: string | number;
  include?: string[];
}

const catalogUrl = (approved: boolean, id?: string | number) =>
  new URL(
    `/v2/${approved ? "approved-services" : "services"}${
      id != null ? `/${id}` : ""
    }`,
    process.env.REACT_APP_SCOPESTACK_API_HOST
  );

const appendInclude = (url: URL, approved: boolean, include?: string[]) => {
  const paths = approved ? translateInclude(include) : include;
  if (paths?.length) url.searchParams.append("include", paths.join(","));
};

export const listCatalogServices = async ({
  accessToken,
  approved,
  include,
  filter,
  page,
  sort,
}: ListArgs) => {
  const url = catalogUrl(approved);
  appendInclude(url, approved, include);
  Object.entries(filter ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    url.searchParams.append(
      `filter[${key}]`,
      Array.isArray(value) ? value.join(",") : String(value)
    );
  });
  if (page) {
    url.searchParams.append("page[number]", String(page.number));
    url.searchParams.append("page[size]", String(page.size));
  }
  if (sort?.length) url.searchParams.append("sort", sort.join(","));

  try {
    const response = await API.Get(url.toString(), accessToken);
    const body = approved ? normalizeApproved(response.data) : response.data;
    return {
      data: body.data,
      included: body.included ?? [],
      meta: body.meta,
      error: null,
    };
  } catch (error) {
    return { data: null, included: [], meta: undefined, error };
  }
};

export const fetchCatalogService = async ({
  accessToken,
  approved,
  id,
  include,
}: ShowArgs) => {
  const url = catalogUrl(approved, id);
  appendInclude(url, approved, include);

  try {
    const response = await API.Get(url.toString(), accessToken);
    const body = approved ? normalizeApproved(response.data) : response.data;
    return {
      data: body.data,
      included: body.included ?? [],
      error: null,
      notApproved: false,
    };
  } catch (error: any) {
    // With the flag on, a missing snapshot means "never approved", which the
    // caller treats as unavailable rather than as a failure.
    if (approved && error?.response?.status === 404) {
      return { data: null, included: [], error: null, notApproved: true };
    }
    return { data: null, included: [], error, notApproved: false };
  }
};
